// Read-only completion identities. Keep launch settings in chat_form.js unchanged.
// Rules ported from mobile CompletionService and ActivityTypeMapper.
const EXERCISE_TYPES = [
  'ask_me_multiple_choice_questions', 'fill_in_the_blanks',
  'translate_sentences_to_native', 'choose_a_tense_and_translate_sentences',
  'ask_me_open_ended_questions', 'verb_focus',
  'ask_a_grammar_question_explanations_in_english',
  'ask_a_grammar_question_explanations_in_target_language'
];
const TRACKABLE_TYPES = new Set([
  'beginner_course', 'grammar', 'roleplay_travel', 'roleplay_integration',
  'roleplay_work', 'roleplay_general', 'vocab_chat', 'debate'
]);
const COURSE_PREFIX = 'beginner_level_conversation.scenarios.';

function isGrammarActivity(key) {
  return typeof key === 'string' && !!key &&
    (key.includes('grammar') || (!key.startsWith('beginner_level_conversation') &&
      EXERCISE_TYPES.some(type => key.includes(type))));
}

function isParentCategory(key) {
  // The web custom-grammar launch stores exercise before the custom suffix.
  if (typeof key === 'string' && EXERCISE_TYPES.some(type => key === `grammar.by_level.${type}.custom`)) return false;
  return typeof key === 'string' && key.startsWith('grammar') &&
    !EXERCISE_TYPES.some(type => key.endsWith(type));
}

function extractGrammarConcept(key) {
  if (typeof key !== 'string') return null;
  const parts = key.split('.');
  const exerciseIndex = parts.findIndex(part => EXERCISE_TYPES.includes(part));
  if (exerciseIndex < 1) return null;
  // Mobile's grammar_* prefix, plus the server's older grammar.by_level form.
  const start = parts[0] === 'grammar' && parts[1] === 'by_level' ? 2 :
    parts.length >= 3 && parts[0].startsWith('grammar') ? 1 : 0;
  return parts.slice(start, exerciseIndex).join('.') || null;
}

function grammarConceptsMatch(left, right) {
  if (typeof left !== 'string' || typeof right !== 'string' || !left || !right) return false;
  const normalize = value => value.toLowerCase().trim().replace(/\s+/g, ' ');
  const a = normalize(left), b = normalize(right);
  return !!a && !!b && (a.includes(b) || b.includes(a));
}

// A row with children is navigation, even when its key resembles an activity.
function activityForSettings(settings = {}, hasChildren = false) {
  const { topic_category: category, topic, subtopic_category: subtopic = '' } = settings;
  if (hasChildren || typeof topic !== 'string' || !topic ||
    ['manual_selection', 'grammar_explanation', 'custom_topic', 'custom_chose_a_topic'].includes(topic)) return null;
  switch (category) {
    case 'guided':
      return { activity_type: 'beginner_course', chat_key: `${COURSE_PREFIX}${topic}` };
    case 'roleplay': {
      if (!subtopic) return null;
      const types = { travel: 'travel', living_in_a_new_country: 'integration', work_and_business: 'work' };
      return { activity_type: `roleplay_${types[subtopic] || 'general'}`, chat_key: `roleplay.${subtopic}.${topic}` };
    }
    case 'debate':
      return { activity_type: 'debate', chat_key: `debate.${topic}` };
    case 'vocab': {
      const vocab = settings.selected_vocab;
      const words = Array.isArray(vocab) ? vocab : typeof vocab === 'string' ? vocab.split(',').map(word => word.trim()) : [];
      return { activity_type: 'vocab_chat', chat_key: `vocab_chat.${words.length ? words.join('_') : topic}` };
    }
    default:
      if (typeof category !== 'string' || !category.startsWith('grammar') || !EXERCISE_TYPES.includes(topic)) return null;
      if (category.includes('/')) {
        return { activity_type: 'grammar', chat_key: `${category.slice(category.indexOf('/') + 1)}.${topic}` };
      }
      // Custom grammar uses the server's exact identity; there is no concept
      // to share across exercises when the learner supplies it after launch.
      return { activity_type: 'grammar', chat_key: category === 'grammar' && subtopic ? `grammar.by_level.${topic}.${subtopic}` : topic };
  }
}

function canonicalKey(activity, lessonAliases) {
  const key = activity.chat_key;
  if (activity.activity_type === 'beginner_course' && key.startsWith(COURSE_PREFIX)) {
    const lesson = key.slice(COURSE_PREFIX.length);
    const current = Object.keys(lessonAliases).find(name => lessonAliases[name].includes(lesson));
    return COURSE_PREFIX + (current || lesson);
  }
  // Recommendations and direct launches have historically used both forms.
  if (activity.activity_type === 'debate') return key.replace(/^debate\.topics\./, 'debate.');
  return key;
}

function isCompleted(activity, histories, language, lessonAliases = {}) {
  if (!language || !activity || !TRACKABLE_TYPES.has(activity.activity_type) ||
    typeof activity.chat_key !== 'string' || !activity.chat_key ||
    isParentCategory(activity.chat_key)) return false;

  const grammar = isGrammarActivity(activity.chat_key) &&
    ['grammar', 'beginner_course'].includes(activity.activity_type);
  const concept = grammar && extractGrammarConcept(activity.chat_key);
  return (Array.isArray(histories) ? histories : []).some(history => {
    if (!history || history.status !== 'completed' || typeof history.chat_key !== 'string') return false;
    // NULL/missing language is deliberately included by the shared API for
    // pre-language-tracking history. Explicitly different languages never match.
    if (history.language != null && history.language !== language) return false;
    if (history.activity_type === activity.activity_type &&
      canonicalKey(history, lessonAliases) === canonicalKey(activity, lessonAliases)) return true;
    return !!concept && ['grammar', 'beginner_course'].includes(history.activity_type) &&
      isGrammarActivity(history.chat_key) && grammarConceptsMatch(concept, extractGrammarConcept(history.chat_key));
  });
}

module.exports = { EXERCISE_TYPES, activityForSettings, isCompleted,
  isGrammarActivity, isParentCategory, extractGrammarConcept, grammarConceptsMatch };
